// ============================================================
// DesignPatternsQuiz.jsx
//
// Advanced Software Architecture Mastery Quiz — ProDev Platform
//
// Features:
//   - New Design Patterns & Architecture question bank
//   - Integrated Timer & Result Processing
//   - Covers Creational, Structural, Behavioral, and React Patterns
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Card, Typography,
    LinearProgress, Stack, TextField,
    Fab, Zoom, Tooltip, styled
} from '@mui/material';
import { Link } from 'react-router-dom';

// Icons
import TimerIcon           from '@mui/icons-material/Timer';
import StarIcon            from '@mui/icons-material/Star';
import ArrowForwardIcon    from '@mui/icons-material/ArrowForward';
import ArchitectureIcon    from '@mui/icons-material/Architecture';

// Brand Constants
const BRAND = {
    dark:       '#100720',
    darkCard:   '#1a0f35',
    darkBorder: 'rgba(229,201,95,0.2)',
    gold:       '#E5C95F',
    success:    '#3ECF8E',
    error:      '#FF6B6B',
};

// Quiz Configuration
const QUIZ_CONFIG = {
    totalTimeSeconds: 1200, // 20 minutes
    passMark: 0.80          // 80% to pass
};

// Design Patterns Question Bank
const patternsQuestionBank = [
    {
        question: 'Which pattern ensures that a class has only one instance and provides a global point of access to it?',
        options: ['Factory', 'Singleton', 'Observer', 'Adapter'],
        answer: 'Singleton',
        explanation: 'The Singleton pattern is used for objects that must be unique across the entire application, such as a database connection pool or a global configuration manager.',
        category: 'Creational',
    },
    {
        question: 'You have a legacy API that returns data in a format your modern UI cannot read. Which pattern acts as a "middleman" to translate the data?',
        options: ['Facade', 'Proxy', 'Adapter', 'Decorator'],
        answer: 'Adapter',
        explanation: 'The Adapter pattern translates one interface into another, allowing incompatible classes to work together without changing their source code.',
        category: 'Structural',
    },
    {
        question: 'Which pattern allows a "Subject" to notify multiple "Observers" about state changes without being tightly coupled to them?',
        options: ['Strategy', 'Command', 'Observer', 'State'],
        answer: 'Observer',
        explanation: 'This is the foundation of React state management and Redux. When the data changes, the UI "observes" the change and updates automatically.',
        category: 'Behavioral',
    },
    {
        question: 'You want to add "Loading" or "Error Handling" functionality to several different React components. Which React-specific pattern should you use?',
        options: ['Compound Components', 'Higher-Order Components (HOC)', 'Prop Drilling', 'Singleton Component'],
        answer: 'Higher-Order Components (HOC)',
        explanation: 'An HOC is a function that takes a component and returns a new component with added functionality, effectively acting as a Decorator.',
        category: 'React Patterns',
    },
    {
        question: 'Which pattern is best suited for building an Undo/Redo system by turning actions into standalone objects?',
        options: ['Command', 'State', 'Proxy', 'Factory'],
        answer: 'Command',
        explanation: 'The Command pattern encapsulates a request as an object, allowing you to store a history of actions and reverse them if necessary.',
        category: 'Behavioral',
    },
    {
        question: 'You need to create a complex "User" object with 15 optional parameters. Which pattern prevents "Constructor Hell"?',
        options: ['Prototype', 'Builder', 'Facade', 'Strategy'],
        answer: 'Builder',
        explanation: 'The Builder pattern allows you to construct complex objects step-by-step, making the code more readable and maintaining argument order.',
        category: 'Creational',
    },
    {
        question: 'Which pattern provides a simplified interface to a complex system of classes (like a single "Checkout" method for payment, inventory, and shipping)?',
        options: ['Adapter', 'Proxy', 'Facade', 'Bridge'],
        answer: 'Facade',
        explanation: 'A Facade hides the complexity of underlying subsystems, providing a simple, high-level entry point for the client.',
        category: 'Structural',
    },
    {
        question: 'In React, the <Tabs> and <Tab> components work together sharing state via context. This is known as which pattern?',
        options: ['Higher-Order Components', 'Compound Components', 'Factory Components', 'Render Props'],
        answer: 'Compound Components',
        explanation: 'Compound Components allow children to implicitly share state with their parent, providing a highly flexible and expressive API.',
        category: 'React Patterns',
    }
];

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

export default function DesignPatternsQuiz() {
    const [phase, setPhase] = useState('welcome');
    const [name, setName] = useState('');
    const [questions, setQuestions] = useState([]);
    const [current, setCurrent] = useState(0);
    const [score, setScore] = useState(0);
    const [selected, setSelected] = useState(null);
    const [showAnswer, setShowAnswer] = useState(false);
    const [finished, setFinished] = useState(false);
    const [timer, setTimer] = useState(QUIZ_CONFIG.totalTimeSeconds);

    const startQuiz = () => {
        setQuestions(shuffle(patternsQuestionBank).map(q => ({ ...q, options: shuffle(q.options) })));
        setPhase('quiz');
    };

    useEffect(() => {
        if (phase === 'quiz' && !finished && timer > 0) {
            const id = setInterval(() => setTimer(t => t - 1), 1000);
            return () => clearInterval(id);
        } else if (timer === 0) {
            setFinished(true);
        }
    }, [phase, finished, timer]);

    const handleAnswer = (opt) => {
        if (showAnswer) return;
        setSelected(opt);
        setShowAnswer(true);
        if (opt === questions[current].answer) setScore(s => s + 1);
    };

    const handleNext = () => {
        if (current + 1 < questions.length) {
            setCurrent(c => c + 1);
            setShowAnswer(false);
            setSelected(null);
        } else {
            setFinished(true);
        }
    };

    if (finished) {
        const passed = (score / questions.length) >= QUIZ_CONFIG.passMark;
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: BRAND.dark, p: 4, display: 'flex', alignItems: 'center', color: 'white' }}>
                <Card sx={{ maxWidth: 600, mx: 'auto', bgcolor: BRAND.darkCard, color: 'white', p: 4, borderRadius: 5, border: `1px solid ${BRAND.darkBorder}`, textAlign: 'center' }}>
                    <ArchitectureIcon sx={{ fontSize: 60, color: BRAND.gold, mb: 2 }} />
                    <Typography variant="h4" fontWeight={800} gutterBottom>Architecture Exam Results</Typography>
                    <Typography variant="h6" color={BRAND.gold} sx={{ mb: 3 }}>Architect: {name}</Typography>

                    <Box sx={{ my: 3, display: 'flex', justifyContent: 'center', gap: 1 }}>
                        {[1, 2, 3].map(i => {
                            const ratio = score / questions.length;
                            const active = (i === 1 && ratio >= 0.8) || (i === 2 && ratio >= 0.9) || (i === 3 && ratio >= 1.0);
                            return <StarIcon key={i} sx={{ fontSize: 50, color: active ? BRAND.gold : 'rgba(255,255,255,0.1)' }} />;
                        })}
                    </Box>

                    <Typography variant="h5" sx={{ mb: 1 }}>{score} / {questions.length} Correct</Typography>
                    <Typography variant="body1" sx={{ opacity: 0.7, mb: 4 }}>
                        {passed
                            ? "Masterful performance. You have demonstrated a deep understanding of structural and behavioral architecture."
                            : "You did not meet the 80% threshold for certification. Architecture requires precision—review the curriculum and try again."}
                    </Typography>

                    <Button component={Link} to="/design-patterns-dashboard" variant="contained" sx={{ bgcolor: BRAND.gold, color: BRAND.dark, fontWeight: 700, px: 4 }}>
                        Return to Hub
                    </Button>
                </Card>
            </Box>
        );
    }

    if (phase === 'welcome') {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: BRAND.dark, p: 4, display: 'flex', alignItems: 'center', color: 'white' }}>
                <Card sx={{ maxWidth: 500, mx: 'auto', bgcolor: BRAND.darkCard, color: 'white', p: 4, borderRadius: 5, border: `1px solid ${BRAND.darkBorder}` }}>
                    <Typography variant="h5" fontWeight={800} textAlign="center" mb={2}>Design Patterns Exam</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7, mb: 3, textAlign: 'center' }}>
                        This exam will test your ability to apply patterns to real-world engineering scenarios.
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="Your Full Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, mb: 3, input: { color: 'white' } }}
                    />
                    <Button fullWidth onClick={startQuiz} disabled={!name} variant="contained" sx={{ bgcolor: BRAND.gold, color: BRAND.dark, fontWeight: 700 }}>
                        Start Certification
                    </Button>
                </Card>
            </Box>
        );
    }

    const q = questions[current];
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BRAND.dark, p: 4, color: 'white' }}>
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
                    <Typography color={BRAND.gold} fontWeight={700}>Question {current + 1}/{questions.length}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TimerIcon sx={{ color: BRAND.error, fontSize: 20 }} />
                        <Typography color={BRAND.error} fontWeight={700}>
                            {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
                        </Typography>
                    </Stack>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={(current / questions.length) * 100}
                    sx={{ height: 10, borderRadius: 5, mb: 4, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: BRAND.gold } }}
                />

                <Card sx={{ bgcolor: BRAND.darkCard, color: 'white', p: 4, borderRadius: 5, border: `1px solid ${BRAND.darkBorder}` }}>
                    <Typography variant="h6" mb={4} sx={{ lineHeight: 1.5 }}>{q.question}</Typography>
                    <Stack spacing={2}>
                        {q.options.map((opt, i) => (
                            <Button
                                key={i}
                                onClick={() => handleAnswer(opt)}
                                disabled={showAnswer}
                                sx={{
                                    justifyContent: 'flex-start', textAlign: 'left', p: 2, borderRadius: 3,
                                    border: `1px solid ${showAnswer && opt === q.answer ? BRAND.success : showAnswer && opt === selected ? BRAND.error : 'rgba(255,255,255,0.1)'}`,
                                    color: 'white', bgcolor: 'rgba(255,255,255,0.03)',
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                                    '&.Mui-disabled': { color: 'white', opacity: 1 }
                                }}
                            >
                                {opt}
                            </Button>
                        ))}
                    </Stack>

                    {showAnswer && (
                        <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 3 }}>
                            <Typography variant="subtitle2" color={selected === q.answer ? BRAND.success : BRAND.error} fontWeight={800} mb={1}>
                                {selected === q.answer ? '✓ SYSTEM CORRECT' : `✗ ARCHITECTURE MISMATCH`}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>{q.explanation}</Typography>
                            <Button variant="contained" onClick={handleNext} sx={{ bgcolor: BRAND.gold, color: BRAND.dark, fontWeight: 700 }} endIcon={<ArrowForwardIcon />}>
                                {current + 1 === questions.length ? "Finish Exam" : "Next Principle"}
                            </Button>
                        </Box>
                    )}
                </Card>
            </Box>
        </Box>
    );
}
